import {
  Button,
  SlideOutModal,
  SlideOutModalProps,
} from "../../../components";
import { Form, Button as BSButton, Card } from "react-bootstrap";
import * as yup from 'yup';
import { ErrorMessage, Formik } from 'formik';
import { useRef } from "react";
import { sharedProperty, useAsync, useSharedState } from "../../../hooks";
import ServiceModel from "../../../data/entities/booking/ServiceModel";
import ProviderSelection from "../components/ProviderSelection";
import { ProviderService, ServiceLocationService } from "../../../services";
import { BookingModel } from "../../../data/entities/booking/BookingModel";
import { BookingEventModel } from "../../../data/entities/booking/BookingEventModel";
import { ProviderModel } from "../../../data/entities";
import { formatPrice, getChargeLabel } from "../../../utils";
import { SpinnerDefault } from "../../../components";

export interface BookServiceModalProps extends SlideOutModalProps {
  onSubmit: any;
  service: sharedProperty<ServiceModel>;
  booking: sharedProperty<BookingModel>;
  locationId: number;
}

export function BookServiceModal(props: Readonly<BookServiceModalProps>) {
  const submitRef = useRef<HTMLButtonElement>(null);
  const providerService = new ProviderService();
  const serviceLocationService = new ServiceLocationService();
  
  const { value: providers } = useAsync(
    () => providerService.getByLocationService(props.locationId, props.service.value.id), 
    [props.locationId, props.service.value.id]
  );

  const sortedProviders = [...(providers ?? [])].sort(
    (a, b) => (a.priority ?? 0) - (b.priority ?? 0) || a.name.localeCompare(b.name)
  );
  
  const { value: servicePriceInfo, loading: priceLoading } = useAsync(
    () => serviceLocationService.getEffectiveServicePriceAsync(props.service.value.id, props.locationId),
    [props.service.value.id, props.locationId]
  );

  const eventProvider = useSharedState<ProviderModel | null>(null);
  
  const validationSchema = yup.object().shape({
    serviceId: yup.number().required(),
    providerId: props.service.value.hideProviders
      ? yup.number().notRequired()
      : props.service.value.allowAnyProvider
      ? yup.number()
          .required('Please select a provider')
          .min(-1, 'Please select a provider')
          .typeError('Please select a provider')
      : yup.number()
          .required('Please select a provider')
          .moreThan(0, 'Please select a provider')
          .typeError('Please select a provider'),
  });

  const chargeAmount = servicePriceInfo?.finalChargeAmount ?? 0;
  const servicePrice = servicePriceInfo?.effectivePrice || props.service.value.defaultPrice;

  return (
    <SlideOutModal
      {...props}
      title="Book Service"
      footer={
        <div className="container p-0">
          <div className="row g-0">
            <div className="col-12 pe-2">
              <Button onClick={() => props.showState.setter(false)} variant="cancel" className="w-100">
                Cancel
              </Button>
            </div>
            <div className="col-12 ps-2">
              <Button onClick={() => submitRef.current?.click()} variant="primary" className="w-100">
                Add to Booking
              </Button>
            </div>
          </div>
        </div>
      }
    >
      {priceLoading ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <SpinnerDefault />
        </div>
      ) : (
        <>
          {/* Service Info Card */}
          <Card className="mb-3 shadow-sm">
            <Card.Body className="p-3">
              <h4 className="mb-3 fw-bold">{props.service.value.name}</h4>
              
              {props.service.value.description && (
                <div className="mb-3">
                  <p className="mb-0">{props.service.value.description}</p>
                </div>
              )}

              {/* Pricing Section */}
              <div className="p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted">Service Price</span>
                  <span >{formatPrice(servicePrice)}</span>
                </div>

                <hr className="my-2" />
                
                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-primary small">
                    <i className="fa fa-arrow-right me-1"></i>
                    {getChargeLabel(servicePriceInfo?.appliedChargeType)}
                  </span>
                  <span className="text-primary fw-bold">{formatPrice(chargeAmount)}</span>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Provider Selection */}
          {!props.service.value.hideProviders && (
            <div className="p-3 mt-2">
              <h5 className="text-muted mb-3 fw-semibold" style={{ fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Choose a Provider
              </h5>
              <Formik
                validationSchema={validationSchema}
                validateOnChange={true}
                initialValues={{
                  serviceId: props.service.value.id,
                  providerId: undefined as number | undefined,
                }}
                onSubmit={async (values) => {
                  const isAnyProvider = values.providerId === -1;
                  if (!isAnyProvider && (!values.providerId || !eventProvider.value)) {
                    return;
                  }

                  const bookingEvent: BookingEventModel = {
                    id: Date.now(),
                    bookingId: 0,
                    serviceId: props.service.value.id,
                    providerId: isAnyProvider ? null : values.providerId,
                    isAnyProvider: isAnyProvider,
                    service: props.service.value,
                    provider: isAnyProvider ? undefined : eventProvider.value ?? undefined,
                  };
                  props.onSubmit(bookingEvent, servicePrice);
                }}
              >
                {({ handleSubmit, setFieldValue }) => {
                  return (
                    <Form onSubmit={handleSubmit}>
                      <ProviderSelection
                        providers={sortedProviders}
                        showAnyProvider={props.service.value.allowAnyProvider}
                        onSelectAnyProvider={() => {
                          setFieldValue("providerId", -1);
                          eventProvider.setter(null);
                        }}
                        onSelectProvider={(provider) => {
                          setFieldValue("providerId", provider.id);
                          eventProvider.setter(provider);
                        }}
                      />
                      <ErrorMessage name="providerId" component="div" className="text-danger mt-2" />
                      <BSButton 
                        ref={submitRef} 
                        type="submit" 
                        size="lg" 
                        className="d-none"
                      />
                    </Form>
                  )
                }}
              </Formik>
            </div>
          )}

          {props.service.value.hideProviders && (
            <Formik
              validationSchema={validationSchema}
              initialValues={{
                serviceId: props.service.value.id,
                providerId: undefined as number | undefined,
              }}
              onSubmit={async (values) => {
                const bookingEvent: BookingEventModel = {
                  id: Date.now(),
                  bookingId: 0,
                  serviceId: props.service.value.id,
                  providerId: values.providerId,
                  service: props.service.value,
                  provider: eventProvider.value || undefined,
                };
                props.onSubmit(bookingEvent, servicePrice);
              }}
            >
              {({ handleSubmit }) => {
                return (
                  <Form onSubmit={handleSubmit}>
                    <BSButton 
                      ref={submitRef} 
                      type="submit" 
                      size="lg" 
                      className="d-none"
                    />
                  </Form>
                )
              }}
            </Formik>
          )}
        </>
      )}
    </SlideOutModal>
  );
}

export default BookServiceModal;